/**
 * File: documentsUtils.ts
 * Path: apps/admin/src/shared/utils/documentsUtils.ts
 * Description: Rules for files attached to a lead or client: where they are
 * stored, which are allowed (mirrors storage.rules), and how they read in a list.
 */

import type { DocumentOwnerKind } from "@idk/config";

export interface DocumentFolder {
  kind: DocumentOwnerKind;
  id: string;
}

export interface AttachedDocument {
  path: string;
  name: string;
  url: string;
  size: number;
  uploadedAt: number;
  /** "audit" for an audit report attached on the Website audit tab. */
  tag: string;
  /** Which record's folder it sits in. */
  owner: DocumentFolder;
  /** Set when the file is listed in businesses/{businessId}/documents/{indexId}. */
  index?: { businessId: string; indexId: string };
}

/** Must match the size limit in storage.rules. */
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;

/** Must match the content types allowed in storage.rules. */
const ALLOWED_TYPE =
  /^(application\/pdf|image\/.*|text\/(plain|csv)|application\/(msword|vnd\.ms-excel|vnd\.openxmlformats-officedocument\..*))$/;

export const DOCUMENT_ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.webp,.heic,.txt,.csv,.doc,.docx,.xls,.xlsx";

/** Why a file can't be attached, or null when it can. */
export const documentProblem = (
  file: Pick<File, "name" | "size" | "type">,
): string | null => {
  if (file.size === 0) return `${file.name} is empty.`;
  if (file.size > MAX_DOCUMENT_BYTES) return `${file.name} is over 25 MB.`;
  if (!ALLOWED_TYPE.test(file.type)) {
    return `${file.name} isn't a type that can be attached (PDF, picture, Word, Excel, text).`;
  }
  return null;
};

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** The name Liam gave the file: kept in metadata, else the stored name minus its timestamp. */
export const displayName = (
  storedName: string,
  original: string | undefined,
): string => original?.trim() || storedName.replace(/^\d+-/, "");

/** Two files with the same key are the same document (shared by listing and delete). */
export const documentKey = (d: Pick<AttachedDocument, "name" | "size">): string =>
  `${d.name}|${d.size}`;

/**
 * Indexed documents first, then old-folder files that are not indexed yet. The
 * same name and size counts as one file (the move copies, so both exist for a while).
 */
export const mergeDocuments = (
  indexed: readonly AttachedDocument[],
  legacy: readonly AttachedDocument[],
): AttachedDocument[] => {
  const seen = new Set(indexed.map(documentKey));
  const extra = legacy.filter((d) => {
    const key = documentKey(d);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return [...indexed, ...extra].sort((a, b) => b.uploadedAt - a.uploadedAt);
};
